import React, { useState, useEffect } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Shield, UserCheck, UserX } from "lucide-react";

const ALL_PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "agents", label: "Agent Management" },
  { key: "clients", label: "Clients" },
  { key: "b2c_reports", label: "B2C Reports" },
  { key: "broadcast_reports", label: "Broadcast Reports" },
  { key: "csv_upload", label: "CSV Upload" },
  { key: "auto_dialer", label: "Auto Dialer" },
  { key: "dialpad", label: "Dialpad" },
  { key: "call_logs", label: "Call Logs" },
  { key: "conversions", label: "Conversions" },
  { key: "reports", label: "Reports & Analytics" },
  { key: "automations", label: "Automations" },
  { key: "salary", label: "Salary" },
  { key: "settings", label: "Settings" },
  { key: "chat", label: "Team Chat" },
  { key: "extension_download", label: "Chrome Extension Download" },
  { key: "mask_mobile", label: "See Mobile Numbers (unmasked)" },
];

export default function SubAdminPage() {
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: [] });

  const fetchData = async () => {
    try {
      const { data } = await apiCall("get", "/admin/sub-admins");
      setSubAdmins(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const togglePerm = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
    if (form.permissions.length === 0) { toast.error("Select at least one permission"); return; }
    try {
      await apiCall("post", "/admin/create-admin", form);
      toast.success("Sub-Admin created");
      setShowCreate(false);
      setForm({ name: "", email: "", password: "", permissions: [] });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (admin) => {
    try {
      await apiCall("put", `/admin/sub-admins/${admin._id}`, { is_active: !admin.is_active });
      toast.success("Updated");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const handleEditPerms = async () => {
    if (!editAdmin) return;
    try {
      await apiCall("put", `/admin/sub-admins/${editAdmin._id}`, { permissions: form.permissions, name: form.name });
      toast.success("Permissions updated");
      setEditAdmin(null);
      fetchData();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="sub-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Sub-Admin Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Create admins with limited permissions</p>
        </div>
        <Button onClick={() => { setForm({ name: "", email: "", password: "", permissions: [] }); setShowCreate(true); }} data-testid="create-sub-admin-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Sub-Admin
        </Button>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase">Name</TableHead>
                <TableHead className="text-xs uppercase">Email</TableHead>
                <TableHead className="text-xs uppercase">Permissions</TableHead>
                <TableHead className="text-xs uppercase">Status</TableHead>
                <TableHead className="text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subAdmins.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No sub-admins created</TableCell></TableRow>
              ) : subAdmins.map((a, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium text-sm">{a.name}</TableCell>
                  <TableCell className="text-sm">{a.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(a.permissions || []).map(p => <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>)}
                    </div>
                  </TableCell>
                  <TableCell><Badge className={a.is_active ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"}>{a.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="text-xs text-primary" onClick={() => { setEditAdmin(a); setForm({ name: a.name, email: a.email, password: "", permissions: a.permissions || [] }); }}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => toggleActive(a)}>
                        {a.is_active ? <UserX className="w-3 h-3 mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
                        {a.is_active ? "Off" : "On"}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-xs text-destructive" onClick={async () => { if (window.confirm("Delete?")) { try { await apiCall("delete", `/admin/sub-admins/${a._id}`); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); } } }}>
                        Del
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-sub-admin-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Create Sub-Admin</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9" data-testid="sub-admin-name" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="h-9" data-testid="sub-admin-email" /></div>
              <div><Label className="text-xs">Password</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="h-9" data-testid="sub-admin-password" /></div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Permissions (Select sections this admin can access)</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_PERMISSIONS.map(p => (
                  <div key={p.key} className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted/50 cursor-pointer" onClick={() => togglePerm(p.key)}>
                    <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} />
                    <span className="text-sm">{p.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreate} data-testid="save-sub-admin-btn">Create Admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Permissions Dialog */}
      <Dialog open={!!editAdmin} onOpenChange={() => setEditAdmin(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-perms-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Edit Permissions - {editAdmin?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9" /></div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Permissions</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_PERMISSIONS.map(p => (
                  <div key={p.key} className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted/50 cursor-pointer" onClick={() => togglePerm(p.key)}>
                    <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} />
                    <span className="text-sm">{p.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditAdmin(null)}>Cancel</Button>
            <Button size="sm" onClick={handleEditPerms}>Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
